import type { ReportInterval } from '../../types/reports';
import { ReportInterval as Interval } from '../../types/reports';

/**
 * Reine Auswahl- und URL-Logik fuer `PeriodFilter` (D-11). Kennt weder eine UI-Bibliothek noch
 * Hooks -- ausschliesslich Funktionen, die von URL-Suchparametern (bzw. deren Entsprechung) auf
 * Anzeigeoptionen, Vorbelegung und neue Suchparameter abbilden. Dadurch bleibt die gesamte
 * Entscheidungslogik ohne Browser testbar; `PeriodFilter.tsx` ruft nur noch durch.
 *
 * `params` ist bewusst ein einfaches `Record<string, string | null>` -- die Komponente reicht die
 * Werte aus `useSearchParams` unveraendert durch, dieses Modul kennt den Aufrufer nicht.
 */

const MONTH_NAMES_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export interface PeriodOption {
  value: string;
  label: string;
}

/**
 * Name des Periodenparameters je Intervall. `null` bei jaehrlich -- eine jaehrliche Meldung
 * kennt nur das Jahr, keinen zweiten Parameter. Damit bleibt jede heutige URL, die eine
 * Auswertungsseite liest, unveraendert gueltig -- Bedingung, nicht Nebeneffekt (D-11).
 */
function periodParamName(interval: ReportInterval): 'month' | 'quarter' | null {
  switch (interval) {
    case Interval.MONTHLY:
      return 'month';
    case Interval.QUARTERLY:
      return 'quarter';
    case Interval.YEARLY:
      return null;
  }
}

/**
 * Auswahlmoeglichkeiten fuer den Periodenparameter je Intervall.
 * - quartalsweise: fuenf Eintraege, "Alle" zuerst, danach Q1..Q4 (Beschriftungen aus der frueheren
 *   quartalsweisen Filterkomponente uebernommen)
 * - monatlich: 13 Eintraege, "Alle" zuerst, danach die zwoelf Monate
 * - jaehrlich: keine Periodenoptionen -- nur die Jahresauswahl ist relevant
 */
export function periodOptions(interval: ReportInterval): PeriodOption[] {
  switch (interval) {
    case Interval.QUARTERLY:
      return [
        { value: 'all', label: 'Alle' },
        { value: '1', label: 'Q1 (Jan-Mär)' },
        { value: '2', label: 'Q2 (Apr-Jun)' },
        { value: '3', label: 'Q3 (Jul-Sep)' },
        { value: '4', label: 'Q4 (Okt-Dez)' },
      ];
    case Interval.MONTHLY:
      return [
        { value: 'all', label: 'Alle' },
        ...MONTH_NAMES_DE.map((label, index) => ({ value: String(index + 1), label })),
      ];
    case Interval.YEARLY:
      return [];
  }
}

/**
 * Datengestuetzte Vorbelegung des Jahres: das neueste Jahr mit tatsaechlich verfuegbaren Daten,
 * oder das aktuelle Kalenderjahr, wenn keine Jahre verfuegbar sind (D-09).
 */
export function defaultYear(availableYears: number[]): number {
  return availableYears.length > 0 ? Math.max(...availableYears) : new Date().getFullYear();
}

/**
 * Ausgewaehltes Jahr aus den URL-Suchparametern. Faellt auf die datengestuetzte Vorbelegung
 * zurueck, wenn der Parameter fehlt, nicht-numerisch ist, oder ein Jahr benennt, das nicht in
 * `availableYears` steht (T-22-24 -- ein manipulierter Parameter erzeugt keine unsinnige
 * Serverabfrage, sondern faellt still auf ein bekanntes Jahr zurueck).
 */
export function selectedYear(
  params: Record<string, string | null>,
  availableYears: number[]
): number {
  const raw = params.year;
  const parsed = raw ? Number(raw) : NaN;

  if (!raw || Number.isNaN(parsed)) {
    return defaultYear(availableYears);
  }

  if (availableYears.length > 0 && !availableYears.includes(parsed)) {
    return defaultYear(availableYears);
  }

  return parsed;
}

/**
 * Ausgewaehlte Periode aus den URL-Suchparametern, unter dem intervallabhaengigen Parameternamen
 * gelesen (`month`/`quarter`/keiner). Fehlt der Parameter, ist das Ergebnis `'all'` -- bei
 * quartalsweise und monatlich bedeutet das "keine Einschraenkung", bei jaehrlich ist es der
 * einzig moegliche Wert.
 */
export function selectedPeriod(
  params: Record<string, string | null>,
  interval: ReportInterval
): string {
  const paramName = periodParamName(interval);

  if (!paramName) {
    return 'all';
  }

  return params[paramName] ?? 'all';
}

/**
 * Neue URL-Suchparameter fuer Jahr und Periode.
 *
 * WICHTIG: `'all'` LAESST den Periodenparameter WEG, statt ihn auf `'all'` zu setzen.
 * `VersandstatistikPage` und `GebietsaufnahmePage` lesen weiterhin
 * `searchParams.get('quarter') ?? 'all'` und leiten daraus ihre YTD-Zeile ab -- ein gesetzter
 * Parameter `quarter=all` wuerde dort faelschlich als Quartalsnummer interpretiert und die
 * YTD-Zeile beim ersten Laden zum Verschwinden bringen (T-22-25).
 */
export function searchParamsFor(
  year: number,
  period: string,
  interval: ReportInterval
): Record<string, string> {
  const params: Record<string, string> = { year: String(year) };
  const paramName = periodParamName(interval);

  if (paramName && period !== 'all') {
    params[paramName] = period;
  }

  return params;
}
